import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "./FormField";
import { eur } from "../helpers";
import { Pencil, X, Save, Home } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const FIXED_FIELDS = [
  ["condominio", "Spese condominiali"], ["luce", "Luce"], ["gas", "Gas"], ["internet", "Internet"],
  ["tari", "TARI"], ["pulizie", "Pulizie"], ["lavanderia", "Lavanderia"], ["manutenzioni", "Manutenzioni"],
  ["prodotti", "Materiale di consumo"], ["altri", "Altri costi fissi"],
];
const ALL = ["canone", ...FIXED_FIELDS.map(([k]) => k)];
const now = new Date();

export default function EconomicDataEditor({ apartmentId, onChanged }) {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/apartment-costs", { params: { year, month } }).then((r) => {
      const found = (r.data || []).find((c) => c.apartment_id === apartmentId);
      setData(found || { apartment_id: apartmentId, year, month });
    }).catch(() => setData({ apartment_id: apartmentId, year, month }));
  }, [apartmentId, year, month]);
  useEffect(() => { load(); setEditing(false); }, [load]);

  const total = ALL.reduce((s, k) => s + (+data[k] || 0), 0);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { apartment_id: apartmentId, year, month };
      ALL.forEach((k) => { payload[k] = +(data[k] || 0); });
      await api.post("/apartment-costs", payload);
      toast.success("Dati economici salvati");
      setEditing(false); load(); onChanged?.();
    } catch { toast.error("Errore salvataggio"); }
    setSaving(false);
  };

  return (
    <Card className="border-slate-200 p-5" data-testid="economic-data-editor">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-head font-semibold text-slate-900"><Home size={16} className="text-primary" /> Dati economici mensili</h3>
          <p className="text-xs text-slate-500">Canone e costi fissi ricorrenti dell'immobile · modificabili manualmente</p>
        </div>
        <div className="flex items-end gap-2">
          <Field label="Mese">
            <Select value={String(month)} onValueChange={(v) => setMonth(+v)}>
              <SelectTrigger data-testid="econ-month" className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Anno">
            <Select value={String(year)} onValueChange={(v) => setYear(+v)}>
              <SelectTrigger data-testid="econ-year" className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {!editing ? (
            <Button data-testid="econ-edit-btn" variant="outline" onClick={() => setEditing(true)} className="gap-2"><Pencil size={15} /> Modifica dati economici</Button>
          ) : (
            <>
              <Button data-testid="econ-cancel-btn" variant="ghost" onClick={() => { setEditing(false); load(); }} className="gap-2 text-slate-500"><X size={15} /> Annulla</Button>
              <Button data-testid="econ-save-btn" onClick={save} disabled={saving} className="gap-2 bg-primary hover:bg-primary/90"><Save size={15} /> Salva</Button>
            </>
          )}
        </div>
      </div>

      {/* Canone highlighted */}
      <div className="mb-4 rounded-xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase text-primary/70">Canone di locazione (costo fisso mensile)</div>
            <div className="text-xs text-slate-500">Importo garantito al proprietario / affitto passivo dell'immobile</div>
          </div>
          {editing ? (
            <Input data-testid="econ-canone" type="number" step="0.01" className="w-40 text-right" value={data.canone ?? 0} onChange={(e) => set("canone", +e.target.value)} />
          ) : (
            <div className="font-head text-2xl font-semibold text-primary" data-testid="econ-canone-view">{eur(data.canone || 0)}</div>
          )}
        </div>
      </div>

      {/* Fixed costs grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {FIXED_FIELDS.map(([k, label]) => (
          <div key={k} className="rounded-lg border border-slate-200 p-3">
            <div className="mb-1 text-xs text-slate-500">{label}</div>
            {editing ? (
              <Input data-testid={`econ-${k}`} type="number" step="0.01" className="h-8 text-right text-sm" value={data[k] ?? 0} onChange={(e) => set(k, +e.target.value)} />
            ) : (
              <div className="font-medium text-slate-900" data-testid={`econ-${k}-view`}>{eur(data[k] || 0)}</div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end rounded-lg bg-slate-50 px-4 py-3">
        <span className="text-sm text-slate-600">Totale costi fissi mese</span>
        <span className="ml-4 font-head text-lg font-semibold text-amber-700" data-testid="econ-total">{eur(total)}</span>
      </div>
    </Card>
  );
}
